import type { ViewportBehaviorConfig } from '@/config/types/modules/behavior';
import type { PixelRuntimeContext } from '@/runtime/PixelRuntimeContext';

import type {
  NormalizedViewportConfig,
  NormalizedViewportTarget,
  ViewportExitReason,
  ViewportSnapshot,
} from '../contracts/viewport';
import type { BehaviorSignalBus } from '../contracts/signals';
import { describeViewportElement } from '../viewport/describeViewportElement';
import {
  normalizeViewportConfig,
  clamp,
} from '../viewport/normalizeViewportConfig';
import type { ViewportElementState } from '../viewport/ViewportStateStore';
import { ViewportStateStore } from '../viewport/ViewportStateStore';
import { ViewportTargetRegistry } from '../viewport/ViewportTargetRegistry';
import {
  buildThresholds,
  createIntersectionObserver,
} from '../viewport/ViewportObserverFactory';
import { BehaviorTrackerBase } from './BehaviorTrackerBase';

export type ViewportTrackerOptions = {
  readonly signalBus?: BehaviorSignalBus;
  readonly config: ViewportBehaviorConfig;
};

/**
 * Viewport Tracker (Etapa 18) — observa entrada/saída de elementos configurados
 * via `IntersectionObserver`. DOM tracker puro: não conhece Transport, Queue,
 * Sender ou API. Publica sinais internos no BehaviorSignalBus e eventos
 * públicos via BaseEventBuilder + Dispatcher (herdado de BehaviorTrackerBase).
 */
export class ViewportTracker extends BehaviorTrackerBase {
  private readonly signalBus: BehaviorSignalBus | undefined;

  private readonly rawConfig: ViewportBehaviorConfig;

  private normalized: NormalizedViewportConfig | null = null;

  private registry: ViewportTargetRegistry | null = null;

  private stateStore: ViewportStateStore | null = null;

  private observer: IntersectionObserver | null = null;

  private observerAvailable = false;

  private mutationObserver: MutationObserver | null = null;

  private mutationTimer: ReturnType<typeof setTimeout> | null = null;

  private initialized = false;

  private limitWarned = false;

  private enterCount = 0;

  private exitCount = 0;

  private lastEventAt: number | undefined;

  constructor(context: PixelRuntimeContext, options: ViewportTrackerOptions) {
    super(context);
    this.signalBus = options.signalBus;
    this.rawConfig = options.config;
  }

  protected onInit(): void {
    const normalized = normalizeViewportConfig(this.rawConfig);
    this.normalized = normalized;
    this.initialized = true;

    for (const warning of normalized.warnings) {
      this.context.logger.warn(`[Pixel] ${warning.code}: ${warning.message}`);
    }

    if (!normalized.enabled || normalized.targets.length === 0) {
      return;
    }

    if (!this.isDocumentAvailable()) {
      return;
    }

    this.registry = new ViewportTargetRegistry(normalized.maxObservedElements);
    this.stateStore = new ViewportStateStore();

    const thresholds = buildThresholds(normalized.targets);
    const observerResult = createIntersectionObserver(
      entries => this.handleIntersections(entries),
      { rootMargin: normalized.rootMargin, thresholds }
    );

    this.observerAvailable = observerResult.available;

    if (!observerResult.available) {
      this.context.logger.warn(
        `[Pixel] BEHAVIOR_VIEWPORT_UNAVAILABLE: ${observerResult.error ?? 'IntersectionObserver unavailable'}`
      );
      return;
    }

    this.observer = observerResult.observer;
    this.scanAndObserve(normalized.targets);

    if (
      normalized.observeMutations &&
      typeof MutationObserver !== 'undefined'
    ) {
      this.setupMutationObserver(normalized);
    }
  }

  protected onDestroy(): void {
    if (this.mutationTimer) {
      clearTimeout(this.mutationTimer);
      this.mutationTimer = null;
    }
    this.mutationObserver?.disconnect();
    this.mutationObserver = null;

    this.observer?.disconnect();
    this.observer = null;
    this.observerAvailable = false;

    this.stateStore?.clear();
    this.stateStore = null;

    this.registry?.disconnect();
    this.registry = null;

    this.enterCount = 0;
    this.exitCount = 0;
    this.lastEventAt = undefined;
    this.limitWarned = false;
    this.normalized = null;
    this.initialized = false;
  }

  /** Snapshot read-only e serializável para diagnostics/health. */
  getSnapshot(): ViewportSnapshot {
    const normalized = this.normalized;
    const counts = this.stateStore?.counts() ?? { active: 0, pending: 0 };
    const observedElementCount = this.registry?.count() ?? 0;

    return {
      enabled: normalized?.enabled ?? false,
      initialized: this.initialized,
      available: this.observerAvailable,
      targetCount: normalized?.targets.length ?? 0,
      matchedElementCount: observedElementCount,
      observedElementCount,
      activeElementCount: counts.active,
      pendingElementCount: counts.pending,
      enterCount: this.enterCount,
      exitCount: this.exitCount,
      mutationObserverEnabled: this.mutationObserver !== null,
      maxObservedElements: normalized?.maxObservedElements ?? 0,
      limitReached: this.limitWarned,
      lastEventAt: this.lastEventAt,
    };
  }

  // ---------------------------------------------------------------------
  // Scan / observação inicial e dinâmica
  // ---------------------------------------------------------------------

  private scanAndObserve(targets: readonly NormalizedViewportTarget[]): void {
    for (const target of targets) {
      this.observeTarget(target);
    }
  }

  private observeTarget(target: NormalizedViewportTarget): void {
    if (!this.registry || !this.observer || !this.isDocumentAvailable()) return;

    let elements: NodeListOf<Element>;
    try {
      elements = document.querySelectorAll(target.selector);
    } catch {
      this.context.logger.warn(
        `[Pixel] BEHAVIOR_VIEWPORT_INVALID_SELECTOR: target "${target.key}" has an invalid selector.`
      );
      return;
    }

    elements.forEach(element => this.registerElement(element, target));
  }

  private registerElement(
    element: Element,
    target: NormalizedViewportTarget
  ): void {
    if (!this.registry || !this.observer) return;

    const result = this.registry.tryRegister(element, target);

    if (!result.ok) {
      if (!this.limitWarned) {
        this.limitWarned = true;
        this.context.logger.warn(
          '[Pixel] BEHAVIOR_VIEWPORT_LIMIT_REACHED: maxObservedElements reached.'
        );
      }
      return;
    }

    if (result.isNewElement) {
      this.observer.observe(element);
    }
  }

  private setupMutationObserver(normalized: NormalizedViewportConfig): void {
    if (!this.isDocumentAvailable()) return;
    if (typeof MutationObserver === 'undefined') return;

    const rootNode = document.body ?? document.documentElement;
    if (!rootNode) return;

    try {
      this.mutationObserver = new MutationObserver(() => {
        this.scheduleMutationRescan(normalized);
      });
      this.mutationObserver.observe(rootNode, {
        childList: true,
        subtree: true,
      });
    } catch {
      this.mutationObserver = null;
      this.context.logger.warn(
        '[Pixel] BEHAVIOR_VIEWPORT_OBSERVER_FAILED: MutationObserver init failed.'
      );
    }
  }

  private scheduleMutationRescan(normalized: NormalizedViewportConfig): void {
    if (this.mutationTimer) {
      clearTimeout(this.mutationTimer);
    }

    this.mutationTimer = setTimeout(() => {
      this.mutationTimer = null;
      this.handleMutationRescan(normalized);
    }, normalized.mutationDebounceMs);
  }

  private handleMutationRescan(normalized: NormalizedViewportConfig): void {
    if (!this.registry) return;

    // Hardening 18.1: cleanup ANTES do scan libera capacidade para novos
    // elementos na mesma mutation batch (ex.: maxObservedElements=1).
    this.cleanupDisconnectedElements();
    this.scanAndObserve(normalized.targets);
  }

  private cleanupDisconnectedElements(): void {
    if (!this.registry || !this.stateStore) return;

    const disconnected = this.registry
      .allObserved()
      .filter(entry => !entry.element.isConnected);

    for (const entry of disconnected) {
      this.observer?.unobserve(entry.element);

      for (const target of entry.targets) {
        const state = this.stateStore.get(target.key, entry.elementId);
        if (state) {
          this.finalizeRemovedElement(state, target);
        }
      }

      this.registry.unregister(entry.element);
    }
  }

  private finalizeRemovedElement(
    state: ViewportElementState,
    target: NormalizedViewportTarget
  ): void {
    if (!this.stateStore) return;

    this.stateStore.cancelTimer(state);

    if (state.active) {
      state.exitedAt = Date.now();
      if (target.trackExit) {
        this.emitExit(state, target, 'removed');
      }
    }

    state.active = false;
    state.completed = true;
    this.stateStore.delete(target.key, state.elementId);
  }

  // ---------------------------------------------------------------------
  // IntersectionObserver callback / máquina de enter-exit
  // ---------------------------------------------------------------------

  private handleIntersections(entries: IntersectionObserverEntry[]): void {
    if (!this.registry || !this.stateStore) return;

    for (const entry of entries) {
      const registryEntry = this.registry.getByElement(entry.target);
      if (!registryEntry) continue;

      for (const target of registryEntry.targets) {
        this.processEntry(entry, registryEntry.elementId, target);
      }
    }
  }

  private processEntry(
    entry: IntersectionObserverEntry,
    elementId: string,
    target: NormalizedViewportTarget
  ): void {
    if (!this.stateStore) return;

    const state = this.stateStore.getOrCreate(
      target.key,
      target.selector,
      elementId
    );
    if (state.completed) return;

    const ratio = clamp(entry.intersectionRatio, 0, 1);
    state.lastRatio = ratio;
    if (ratio > state.maxRatio) state.maxRatio = ratio;

    if (!state.active) {
      const isCandidate =
        entry.isIntersecting && ratio >= target.enterThreshold;
      this.handleCandidateEnter(state, target, isCandidate);
    } else {
      this.handleActiveElement(state, target, entry, ratio);
    }
  }

  private handleCandidateEnter(
    state: ViewportElementState,
    target: NormalizedViewportTarget,
    isCandidate: boolean
  ): void {
    if (!this.stateStore) return;

    if (!isCandidate) {
      if (state.candidateEnteredAt !== undefined) {
        this.stateStore.cancelTimer(state);
        state.candidateEnteredAt = undefined;
      }
      return;
    }

    if (state.candidateEnteredAt !== undefined) {
      return;
    }

    state.candidateEnteredAt = Date.now();

    if (target.minVisibleMs === 0) {
      this.confirmEnter(state, target);
      return;
    }

    state.pendingTimer = setTimeout(() => {
      state.pendingTimer = null;
      this.confirmEnter(state, target);
    }, target.minVisibleMs);
  }

  private confirmEnter(
    state: ViewportElementState,
    target: NormalizedViewportTarget
  ): void {
    if (state.active || state.completed) return;

    state.active = true;
    state.enteredAt = Date.now();
    state.candidateEnteredAt = undefined;

    this.emitEnter(state, target);

    if (target.once && !target.trackExit) {
      state.completed = true;
      this.finishTargetCycle(state, target);
    }
  }

  private handleActiveElement(
    state: ViewportElementState,
    target: NormalizedViewportTarget,
    entry: IntersectionObserverEntry,
    ratio: number
  ): void {
    const shouldExit = !entry.isIntersecting || ratio <= target.exitThreshold;
    if (!shouldExit) return;

    const reason: ViewportExitReason = !entry.isIntersecting
      ? 'not_intersecting'
      : 'threshold';

    this.completeCycle(state, target, reason);
  }

  private completeCycle(
    state: ViewportElementState,
    target: NormalizedViewportTarget,
    reason: ViewportExitReason
  ): void {
    if (!this.stateStore) return;

    this.stateStore.cancelTimer(state);
    state.active = false;
    state.exitedAt = Date.now();

    if (target.trackExit) {
      this.emitExit(state, target, reason);
    }

    if (target.once) {
      state.completed = true;
      this.finishTargetCycle(state, target);
    } else {
      state.enteredAt = undefined;
      state.exitedAt = undefined;
      state.candidateEnteredAt = undefined;
    }
  }

  /** Remove a associação target↔elemento; unobserve só quando nenhum outro target precisar dele. */
  private finishTargetCycle(
    state: ViewportElementState,
    target: NormalizedViewportTarget
  ): void {
    if (this.registry) {
      const entry = this.registry.getById(state.elementId);
      if (entry) {
        entry.targets.delete(target);
        if (entry.targets.size === 0) {
          this.observer?.unobserve(entry.element);
          this.registry.unregisterById(state.elementId);
        }
      }
    }

    this.stateStore?.delete(target.key, state.elementId);
  }

  // ---------------------------------------------------------------------
  // Eventos públicos + sinais internos
  // ---------------------------------------------------------------------

  private emitEnter(
    state: ViewportElementState,
    target: NormalizedViewportTarget
  ): void {
    const enteredAt = state.enteredAt ?? Date.now();
    const visibilityRatio = clamp(state.lastRatio, 0, 1);
    const element = this.registry?.getById(state.elementId)?.element ?? null;

    this.enterCount += 1;
    this.lastEventAt = enteredAt;

    this.signalBus?.publish(
      this.signalBus.createSignal('VIEWPORT_ENTER', 'ViewportTracker', {
        key: target.key,
        elementId: state.elementId,
        visibilityRatio,
        enteredAt,
      })
    );

    this.dispatchEngagement({
      type: 'VIEWPORT_ENTER',
      category: 'ENGAGEMENT',
      key: target.key,
      selector: target.selector,
      elementId: state.elementId,
      visibilityRatio,
      enterThreshold: target.enterThreshold,
      exitThreshold: target.exitThreshold,
      enteredAt,
      minVisibleMs: target.minVisibleMs,
      ...(target.includeElement
        ? { element: describeViewportElement(element) }
        : {}),
    });
  }

  private emitExit(
    state: ViewportElementState,
    target: NormalizedViewportTarget,
    reason: ViewportExitReason
  ): void {
    const enteredAt = state.enteredAt ?? Date.now();
    const exitedAt = state.exitedAt ?? Date.now();
    const visibleDurationMs = Math.max(0, exitedAt - enteredAt);
    const visibilityRatio = clamp(state.lastRatio, 0, 1);
    const element = this.registry?.getById(state.elementId)?.element ?? null;

    this.exitCount += 1;
    this.lastEventAt = exitedAt;

    this.signalBus?.publish(
      this.signalBus.createSignal('VIEWPORT_EXIT', 'ViewportTracker', {
        key: target.key,
        elementId: state.elementId,
        visibilityRatio,
        enteredAt,
        exitedAt,
        visibleDurationMs,
      })
    );

    this.dispatchEngagement({
      type: 'VIEWPORT_EXIT',
      category: 'ENGAGEMENT',
      key: target.key,
      selector: target.selector,
      elementId: state.elementId,
      visibilityRatio,
      enterThreshold: target.enterThreshold,
      exitThreshold: target.exitThreshold,
      enteredAt,
      exitedAt,
      visibleDurationMs,
      reason,
      ...(target.includeElement
        ? { element: describeViewportElement(element) }
        : {}),
    });
  }
}
